import { useTranslation } from "react-i18next";
import { Alert } from "@/atoms/alert";
import { BrandBar } from "./brand-bar";
import { useOnline } from "./use-online";
import { cx } from "@/utils/cx";
import type { ReactNode } from "react";

/**
 * The shell every money page in this app draws inside (checkout 1.6 layout).
 *
 * Paper soft under a single column, 520 wide. The template says 480 and DSCheckout draws 520;
 * where a sheet and a template disagree the sheet wins (design-system-2027, "where a doc and a
 * sheet disagree, the sheet wins").
 *
 * Paper soft under a single column. At 1024 and 1440 the sheet is 520 wide on paper with a
 * hairline and radius card, centred with 32 above; at 390 it is edge to edge inside the 16
 * margin with no border, because a hairline a thumb's width from the screen edge reads as a
 * mistake rather than as a card. The page never grows past 520 (checkout 1.4 layout.1440:
 * "same as 1024; the page never grows past 520").
 *
 * One `.ds-reveal` for the sheet, once: the template's entrance is the sheet arriving as one
 * block, not seven staggered pieces on a page a person came here to pay on.
 *
 * The offline band sits above everything, under the top of the page, and it is an alert and
 * never a modal (gate 8.3).
 */

export interface PayPageProps {
  children: ReactNode;
  /** The sheet's own padding is dropped for a page that pads its regions itself. */
  padded?: boolean;
  className?: string;
}

export function PayPage({
  children,
  padded = true,
  className,
}: PayPageProps) {
  const { t } = useTranslation();
  const online = useOnline();

  return (
    <div className="bg-surface-paperSoft flex min-h-screen flex-col items-center">
      {!online && (
        <div className="px-gk-4 pt-gk-4 w-full max-w-[520px]">
          <Alert tone="warning">{t("gymkeePay.common.offline")}</Alert>
        </div>
      )}
      <div className="px-gk-4 py-gk-4 sm:py-gk-8 flex w-full flex-col items-center">
        {/* The Gymkee Pay bar sits above the sheet and aligns to its left edge at every width, so
            the bar and the sheet share one column; it is the page's one banner landmark. */}
        <div className="mb-gk-4 sm:mb-gk-6 w-full max-w-[520px]">
          <BrandBar />
        </div>
        <div
          className={cx(
            "ds-reveal flex w-full max-w-[520px] flex-col",
            /* The sheet at 1024 and above; at 390 it is the page itself.

               `ds-hairline` carries no breakpoint prefix on purpose: a Tailwind variant only
               prefixes a Tailwind utility and emits nothing for a class of ours, so `sm:ds-hairline`
               was a rule that never existed and the border fell back to `currentColor` in dark.
               The class only sets a colour, and the border itself is still `sm:` only. */
            "sm:bg-surface-paper ds-hairline sm:rounded-card sm:border",
            padded && "gap-gk-6 sm:p-gk-6",
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
